import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { useContextSelector } from 'use-context-selector';
import { EdiAdminContext } from './EdiAdminContext';

const TIMING_VIEW = 0;
const OUTSTANDING_VIEW = 1;

const formatLastUpdated = (date: Date) =>
  date
    .toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    .replace(', ', ' - ');

const EdiAdminToolbarActions: FC = () => {
  const { t } = useTranslation();
  const selectedView = useContextSelector(EdiAdminContext, (ctx) => ctx.selectedView);
  const hasSelectedTimingLogs = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.hasSelectedTimingLogs
  );
  const hasSelectedOutstandingLogs = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.hasSelectedOutstandingLogs
  );
  const timingLogsRequestedAt = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.timingLogsRequestedAt
  );
  const outstanding810RequestedAt = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.outstanding810RequestedAt
  );
  const rollBackSelectedTimingLogs = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.rollBackSelectedTimingLogs
  );
  const isRollBackTimingLogsPending = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.isRollBackTimingLogsPending
  );
  const resendSelectedTimingLogs = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.resendSelectedTimingLogs
  );
  const isResendTimingLogsPending = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.isResendTimingLogsPending
  );
  const deleteSelectedOutstanding810Logs = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.deleteSelectedOutstanding810Logs
  );
  const isDeleteOutstanding810sPending = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.isDeleteOutstanding810sPending
  );
  const clearFilters = useContextSelector(EdiAdminContext, (ctx) => ctx.clearFilters);
  const hasActiveFilters = useContextSelector(EdiAdminContext, (ctx) => ctx.hasActiveFilters);

  const ediAdminMutationBusy =
    isRollBackTimingLogsPending || isResendTimingLogsPending || isDeleteOutstanding810sPending;

  const timingLogsButtonDisabled = !hasSelectedTimingLogs || ediAdminMutationBusy;
  const timingLogsDisabledHintVisible = timingLogsButtonDisabled && !hasSelectedTimingLogs;

  const deleteButtonDisabled = !hasSelectedOutstandingLogs || ediAdminMutationBusy;
  const deleteDisabledHintVisible = deleteButtonDisabled && !hasSelectedOutstandingLogs;

  const lastUpdatedText = useMemo(() => {
    if (selectedView === TIMING_VIEW && timingLogsRequestedAt > 0) {
      return `${formatLastUpdated(new Date(timingLogsRequestedAt))} PST`;
    }
    if (selectedView === OUTSTANDING_VIEW && outstanding810RequestedAt > 0) {
      return `${formatLastUpdated(new Date(outstanding810RequestedAt))} PST`;
    }
    return '-';
  }, [selectedView, timingLogsRequestedAt, outstanding810RequestedAt]);

  const handleResendSelected = () => {
    void resendSelectedTimingLogs();
  };

  const handleRollBackSelected = () => {
    void rollBackSelectedTimingLogs();
  };

  const handleDeleteSelected810s = () => {
    void deleteSelectedOutstanding810Logs();
  };

  return (
    <div className="flex w-full flex-wrap items-center gap-x-2 gap-y-2 py-1">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <ClearFilterButton onClick={clearFilters} disabled={!hasActiveFilters} />
        {selectedView === TIMING_VIEW && (
          <>
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'center', vertical: 'top' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type="Information" contents={[t('ediResendDisabled')]} />
                </div>
              }
              show={timingLogsDisabledHintVisible}
            >
              <span className="inline-flex">
                <Button
                  color="secondary"
                  size="md"
                  onClick={handleResendSelected}
                  disabled={timingLogsButtonDisabled}
                  loading={isResendTimingLogsPending}
                >
                  {t('Resend Selected')}
                </Button>
              </span>
            </PopUp>
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'center', vertical: 'top' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type="Information" contents={[t('ediRollBackDisabled')]} />
                </div>
              }
              show={timingLogsDisabledHintVisible}
            >
              <span className="inline-flex">
                <Button
                  color="success"
                  size="md"
                  onClick={handleRollBackSelected}
                  disabled={timingLogsButtonDisabled}
                  loading={isRollBackTimingLogsPending}
                >
                  {t('Roll Back Selected')}
                </Button>
              </span>
            </PopUp>
          </>
        )}
        {selectedView === OUTSTANDING_VIEW && (
          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
            anchorAlign={{ horizontal: 'center', vertical: 'top' }}
            className="max-h-[180px]"
            content={
              <div className="m-1 max-w-[350px]">
                <PopUpContentWrapper type="Information" contents={[t('ediDeleteDisabled')]} />
              </div>
            }
            show={deleteDisabledHintVisible}
          >
            <span className="inline-flex">
              <Button
                color="error"
                size="md"
                onClick={handleDeleteSelected810s}
                disabled={deleteButtonDisabled}
                loading={isDeleteOutstanding810sPending}
              >
                {t('Delete Selected 810s')}
              </Button>
            </span>
          </PopUp>
        )}
      </div>
      <div className="w-full shrink-0 text-sm text-white/70 lg:w-auto lg:ml-auto">
        {t('Last Updated on')} {lastUpdatedText}
      </div>
    </div>
  );
};

export default EdiAdminToolbarActions;
